import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

export interface LocationSelectionDialogData {
  locations: string[];
  selectedLocation: string;
}

@Component({
  selector: 'app-location-selection-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
  ],
  template: `
    <h2 mat-dialog-title>Vælg lokation</h2>
    <div mat-dialog-content>
      <mat-form-field appearance="outline" class="search-field">
        <mat-icon matPrefix>search</mat-icon>
        <input
          matInput
          [(ngModel)]="searchText"
          (input)="filterLocations()"
          placeholder="Søg efter lokationer..."
        />
        @if (searchText) {
        <button
          matSuffix
          mat-icon-button
          aria-label="Clear"
          (click)="searchText = ''; filterLocations()"
        >
          <mat-icon>close</mat-icon>
        </button>
        }
      </mat-form-field>

      <div class="locations-grid">
        <div
          class="location-card"
          [class.active]="selectedLocation === 'all'"
          (click)="selectLocation('all')"
        >
          <mat-icon>public</mat-icon>
          <span>Alle lokationer</span>
        </div>

        @for (location of paginatedLocations; track location) {
        <div
          class="location-card"
          [class.active]="selectedLocation === location"
          (click)="selectLocation(location)"
        >
          <mat-icon>location_on</mat-icon>
          <span>{{ location }}</span>
        </div>
        }
      </div>

      <mat-paginator
        [length]="filteredLocations.length"
        [pageSize]="pageSize"
        [pageSizeOptions]="[10, 25, 50]"
        (page)="onPageChange($event)"
      >
      </mat-paginator>
    </div>
    <div mat-dialog-actions align="end">
      <button mat-button (click)="close()">Annuller</button>
      <button mat-raised-button color="primary" (click)="apply()">
        Anvend
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .search-field {
        width: 100%;
        margin-bottom: 16px;
      }

      .locations-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 12px;
        margin-bottom: 16px;
        max-height: min(520px, 58vh);
        overflow-y: auto;
      }

      .location-card {
        background-color: #f5f5f5;
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;

        mat-icon {
          font-size: 24px;
          color: #757575;
        }

        span {
          font-size: 13px;
          font-weight: 500;
        }

        &:hover {
          background-color: #e0e0e0;
        }

        &.active {
          background-color: #e3f2fd;

          mat-icon {
            color: #1976d2;
          }
        }
      }

      /* Fix for paginator dropdown */
      ::ng-deep .mat-mdc-select-panel {
        z-index: 10000 !important;
      }

      ::ng-deep .mat-mdc-select-panel .mat-mdc-option {
        z-index: 10000 !important;
      }

      ::ng-deep .mat-paginator-page-size-select {
        margin-top: 0 !important;
      }

      ::ng-deep .cdk-overlay-container {
        z-index: 10000 !important;
      }

      /* Additional fixes for select panel positioning */
      ::ng-deep .cdk-overlay-pane {
        z-index: 10000 !important;
      }

      ::ng-deep .mat-mdc-paginator {
        position: relative;
        z-index: 1;
      }

      /* Ensure dropdown menu appears above dialog */
      ::ng-deep .mat-mdc-menu-panel {
        z-index: 10001 !important;
      }

      /* Fix for all overlay components */
      ::ng-deep .cdk-global-overlay-wrapper {
        z-index: 1000 !important;
      }

      /* Paginator specific fixes */
      ::ng-deep .mat-mdc-paginator-container {
        z-index: 2;
        position: relative;
      }

      /* More specific selectors to ensure high priority */
      ::ng-deep .mat-mdc-paginator-container .mat-mdc-select-panel {
        z-index: 100000 !important;
      }

      /* Force high z-index for all overlay panels */
      ::ng-deep .cdk-overlay-pane.mat-mdc-select-panel-above {
        z-index: 100001 !important;
      }
    `,
  ],
})
export class LocationSelectionDialogComponent implements OnInit {
  searchText = '';
  filteredLocations: string[] = [];
  paginatedLocations: string[] = [];
  selectedLocation: string;

  // Pagination
  pageSize = 25;
  currentPage = 0;

  constructor(
    public dialogRef: MatDialogRef<LocationSelectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LocationSelectionDialogData
  ) {
    this.selectedLocation = data.selectedLocation;
  }

  ngOnInit(): void {
    this.filteredLocations = [...this.data.locations];
    this.updatePaginatedLocations();
  }

  filterLocations(): void {
    if (!this.searchText) {
      this.filteredLocations = [...this.data.locations];
    } else {
      const searchTerm = this.searchText.toLowerCase();
      this.filteredLocations = this.data.locations.filter((location) =>
        location.toLowerCase().includes(searchTerm)
      );
    }

    // Reset to first page when filtering
    this.currentPage = 0;
    this.updatePaginatedLocations();
  }

  updatePaginatedLocations(): void {
    const startIndex = this.currentPage * this.pageSize;
    this.paginatedLocations = this.filteredLocations.slice(
      startIndex,
      startIndex + this.pageSize
    );
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedLocations();
  }

  selectLocation(location: string): void {
    this.selectedLocation = location;
  }

  apply(): void {
    this.dialogRef.close(this.selectedLocation);
  }

  close(): void {
    this.dialogRef.close();
  }
}
